/**
 * ConsistencyCard — Iter. 406.fr.46
 *
 * Strava-lite unified KPI card for streak-based metrics (Sleep, Hydration).
 * Renders a radial "score %" ring with the current streak + best streak
 * chips. Reusable across pages by feeding a `theme` and label/unit props.
 *
 * Data shape (from /sleep/history or /hydration/history "consistency" field):
 *   {
 *     score_pct: number,              // 0-100
 *     nights_on_target|days_on_target: number,
 *     nights_tracked|days_tracked: number,
 *     target_hours?: number,          // sleep only
 *     current_streak: number,
 *     best_streak: number,
 *   }
 */
import React from 'react';
import { motion } from 'framer-motion';
import { Flame, TrendingUp } from 'lucide-react';

const THEMES = {
  purple: {
    accentHi: '#c084fc', accentLo: '#6b21a8', glow: 'rgba(168,85,247,0.55)',
    ringBg: 'rgba(168,85,247,0.15)',
  },
  blue: {
    accentHi: '#60a5fa', accentLo: '#1e40af', glow: 'rgba(59,130,246,0.55)',
    ringBg: 'rgba(59,130,246,0.15)',
  },
  orange: {
    accentHi: '#fb923c', accentLo: '#7c2d12', glow: 'rgba(249,115,22,0.55)',
    ringBg: 'rgba(249,115,22,0.15)',
  },
};

// Badge tiers for training/hydration/sleep streaks — same across the app
// so the user learns "7 = week, 30 = fer, 90 = titan" once.
const STREAK_TIERS = [
  { min: 90, label_fr: 'Titan',    label_en: 'Titan',    emoji: '💎' },
  { min: 30, label_fr: 'Fer',      label_en: 'Iron',     emoji: '🔥' },
  { min: 14, label_fr: 'Rythme',   label_en: 'Rhythm',   emoji: '⚡' },
  { min: 7,  label_fr: 'Semaine',  label_en: 'Week',     emoji: '✨' },
];

const _tierFor = (n) => STREAK_TIERS.find((t) => n >= t.min) || null;

const ConsistencyCard = ({
  consistency,
  theme = 'purple',
  title,          // e.g. "Sleep Consistency"
  unitLabel,      // e.g. "nuits ≥7h" / "jours à l'objectif"
  streakLabel,    // e.g. "Streak actuel"
  bestLabel,      // e.g. "Meilleure série"
  onTargetLabel,  // e.g. "sur la période"
  streakUnit = 'd', // 'd' (days) | 'w' (weeks)
  fr = true,
  testId = 'consistency-card',
}) => {
  if (!consistency) return null;
  const {
    score_pct = 0,
    nights_on_target,
    days_on_target,
    weeks_on_target,
    nights_tracked,
    days_tracked,
    weeks_tracked,
    current_streak,
    current_streak_weeks,
    best_streak,
    best_streak_weeks,
  } = consistency;
  const onTarget = nights_on_target ?? days_on_target ?? weeks_on_target ?? 0;
  const tracked = nights_tracked ?? days_tracked ?? weeks_tracked ?? 0;
  const curStreak = current_streak ?? current_streak_weeks ?? 0;
  const bestStreakVal = best_streak ?? best_streak_weeks ?? 0;
  const unitShort = streakUnit === 'w' ? (fr ? 'sem' : 'w') : (fr ? 'j' : 'd');

  // Tier badges (only meaningful in "days" mode; weeks streaks stay clean)
  const curTier = streakUnit === 'd' ? _tierFor(curStreak) : null;
  const bestTier = streakUnit === 'd' ? _tierFor(bestStreakVal) : null;

  const t = THEMES[theme] || THEMES.purple;
  const R = 42;
  const C = 2 * Math.PI * R;
  const offset = C - (Math.min(100, score_pct) / 100) * C;

  const level = score_pct >= 70 ? 'high' : score_pct >= 40 ? 'mid' : 'low';
  const scoreColor = level === 'high' ? '#10b981' : level === 'mid' ? '#f59e0b' : '#ef4444';
  const scoreLabel = fr
    ? (level === 'high' ? 'Excellent' : level === 'mid' ? 'À affiner' : 'À rebâtir')
    : (level === 'high' ? 'Excellent' : level === 'mid' ? 'Refine' : 'Rebuild');

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl bg-gradient-to-br from-card/80 via-card/60 to-black/20 ring-1 p-4"
      style={{
        borderColor: t.accentHi,
        boxShadow: `0 0 22px ${scoreColor}22, inset 0 0 24px ${t.glow.replace('0.55','0.06')}`,
        ringColor: t.accentHi,
      }}
      data-testid={testId}
    >
      <div className="flex items-center gap-4">
        {/* Radial score ring */}
        <div className="relative shrink-0 w-24 h-24" data-testid={`${testId}-ring`}>
          <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
            <circle cx="50" cy="50" r={R}
              stroke={t.ringBg} strokeWidth="6" fill="none" />
            <circle cx="50" cy="50" r={R}
              stroke={scoreColor} strokeWidth="6" fill="none"
              strokeLinecap="round"
              strokeDasharray={C}
              strokeDashoffset={offset}
              style={{
                transition: 'stroke-dashoffset 0.8s ease-out',
                filter: `drop-shadow(0 0 6px ${scoreColor})`,
              }}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center leading-none">
            <span
              className="font-heading font-black text-2xl tabular-nums"
              style={{ color: scoreColor, textShadow: `0 0 10px ${scoreColor}88` }}
              data-testid={`${testId}-score`}
            >
              {score_pct}
            </span>
            <span className="text-[9px] uppercase tracking-widest text-muted-foreground mt-0.5">
              %
            </span>
          </div>
        </div>

        {/* Right side */}
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2 mb-2">
            <div>
              <p
                className="font-heading text-sm font-bold uppercase tracking-widest"
                style={{ color: scoreColor }}
              >
                {title}
              </p>
              <p className="text-[11px] text-muted-foreground">
                {onTarget}/{tracked} {unitLabel} · <span style={{ color: scoreColor }}>{scoreLabel}</span>
              </p>
            </div>
          </div>

          {/* Streak chips */}
          <div className="grid grid-cols-2 gap-2" data-testid={`${testId}-streaks`}>
            <div
              className="rounded-lg px-2.5 py-1.5 flex items-center gap-2"
              style={{ background: 'rgba(249,115,22,0.10)', border: '1px solid rgba(249,115,22,0.25)' }}
              data-testid={`${testId}-current-streak`}
            >
              <Flame className="w-3.5 h-3.5 text-orange-400 shrink-0" />
              <div className="min-w-0 leading-tight">
                <p className="text-[9px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
                  {streakLabel}
                  {curTier && (
                    <span
                      className="ml-0.5 text-[10px]"
                      title={fr ? curTier.label_fr : curTier.label_en}
                      data-testid={`${testId}-current-tier`}
                    >
                      {curTier.emoji}
                    </span>
                  )}
                </p>
                <p className="text-sm font-heading font-black text-orange-300 tabular-nums">
                  {curStreak}<span className="text-[10px] text-muted-foreground ml-1">{unitShort}</span>
                </p>
              </div>
            </div>
            <div
              className="rounded-lg px-2.5 py-1.5 flex items-center gap-2"
              style={{ background: 'rgba(52,211,153,0.10)', border: '1px solid rgba(52,211,153,0.25)' }}
              data-testid={`${testId}-best-streak`}
            >
              <TrendingUp className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
              <div className="min-w-0 leading-tight">
                <p className="text-[9px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
                  {bestLabel}
                  {bestTier && (
                    <span
                      className="ml-0.5 text-[10px]"
                      title={fr ? bestTier.label_fr : bestTier.label_en}
                      data-testid={`${testId}-best-tier`}
                    >
                      {bestTier.emoji}
                    </span>
                  )}
                </p>
                <p className="text-sm font-heading font-black text-emerald-300 tabular-nums">
                  {bestStreakVal}<span className="text-[10px] text-muted-foreground ml-1">{unitShort}</span>
                </p>
              </div>
            </div>
          </div>

          <p className="text-[10px] text-muted-foreground/70 mt-1.5">
            {onTargetLabel}
          </p>
        </div>
      </div>
    </motion.div>
  );
};

export default ConsistencyCard;
